import {
  TAX_REGISTRATION_STATUS,
  WORK_LOCATION_TYPE,
  type SaveTaxRegistrationRequest,
  type TaxRegistrationStatusName,
  type WorkLocationRequest,
  type WorkLocationResponse,
  type WorkLocationTypeValue,
} from '../types';

/**
 * The Step 5 Add / Edit Location form as ONE state object spanning both tabs (Branch Details and Payroll Tax Registration), so switching tabs
 * never loses anything and "dirty" means "different from what was loaded (or from a blank form when adding), across both tabs". Pure
 * functions only: validation mirrors the server's rules (the server decides; this only avoids obviously doomed requests).
 *
 * Account numbers are never loaded back (the server returns only a masked value), so their fields always start empty. Leaving one empty
 * sends `null` ("keep what is on file"); typing replaces it.
 */

export interface RegistrationFormState {
  status: TaxRegistrationStatusName | '';
  effectiveDate: string;
  nexusEstablishedDate: string;
  stateAccount: string;
  suiAccount: string;
  sdiPflAccount: string;
  localRegistration: string;
  localAccount: string;
  notes: string;
}

export interface LocationFormState {
  name: string;
  locationType: WorkLocationTypeValue | '';
  activeForPayroll: boolean;
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  city: string;
  county: string;
  zip: string;
  mailingSame: boolean;
  mailingLine1: string;
  mailingLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  mailingCountry: string;
  registration: RegistrationFormState;
}

export const MAX_NOTES_LENGTH = 100;

export const EMPTY_REGISTRATION: RegistrationFormState = {
  status: '',
  effectiveDate: '',
  nexusEstablishedDate: '',
  stateAccount: '',
  suiAccount: '',
  sdiPflAccount: '',
  localRegistration: '',
  localAccount: '',
  notes: '',
};

/** A new location starts blank (Figma S21: Country and State are chosen, not defaulted), "Not Registered" preselected (Figma S22) and Active for Payroll
 * off: turning a location on for employee assignment is an explicit choice. */
export const EMPTY_FORM: LocationFormState = {
  name: '',
  locationType: '',
  activeForPayroll: false,
  addressLine1: '',
  addressLine2: '',
  country: '',
  state: '',
  city: '',
  county: '',
  zip: '',
  mailingSame: true,
  mailingLine1: '',
  mailingLine2: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',
  mailingCountry: '',
  registration: { ...EMPTY_REGISTRATION, status: 'NotRegistered' },
};

export function emptyForm(): LocationFormState {
  return { ...EMPTY_FORM, registration: { ...EMPTY_FORM.registration } };
}

export function formFromLocation(location: WorkLocationResponse): LocationFormState {
  const reg = location.taxRegistration;
  const mailing = location.mailingAddress;
  return {
    name: location.name,
    locationType: location.locationType
      ? ((WORK_LOCATION_TYPE[location.locationType as keyof typeof WORK_LOCATION_TYPE] ?? '') as WorkLocationTypeValue | '')
      : '',
    activeForPayroll: location.activeForPayroll,
    addressLine1: location.addressLine1,
    addressLine2: location.addressLine2 ?? '',
    country: location.country,
    state: location.state,
    city: location.city,
    county: location.county ?? '',
    zip: location.zip,
    mailingSame: mailing.sameAsPhysical,
    mailingLine1: mailing.addressLine1 ?? '',
    mailingLine2: mailing.addressLine2 ?? '',
    mailingCity: mailing.city ?? '',
    mailingState: mailing.state ?? '',
    mailingZip: mailing.zip ?? '',
    mailingCountry: mailing.country ?? 'US',
    registration: {
      // A location with no saved record shows as Not Registered, the status the server reports for it.
      status: reg?.status ?? location.taxRegistrationStatus,
      effectiveDate: reg?.effectiveDate ?? '',
      nexusEstablishedDate: reg?.nexusEstablishedDate ?? '',
      stateAccount: '',
      suiAccount: '',
      sdiPflAccount: '',
      localRegistration: '',
      localAccount: '',
      notes: reg?.notes ?? '',
    },
  };
}

const trimmed = (v: string) => v.trim();

/** The comparable shape of a form: trimmed text, and the mailing fields ignored while "same as physical" is on. */
function comparable(form: LocationFormState) {
  const mailing = form.mailingSame
    ? null
    : [form.mailingLine1, form.mailingLine2, form.mailingCity, form.mailingState, form.mailingZip, form.mailingCountry].map(trimmed);
  return {
    name: trimmed(form.name),
    locationType: form.locationType,
    activeForPayroll: form.activeForPayroll,
    address: [form.addressLine1, form.addressLine2, form.country, form.state, form.city, form.county, form.zip].map(trimmed),
    mailing,
    registration: Object.fromEntries(Object.entries(form.registration).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])),
  };
}

/** True when the form differs from `baseline` on EITHER tab. Reverting every change returns it to clean. */
export function isFormDirty(form: LocationFormState, baseline: LocationFormState): boolean {
  return JSON.stringify(comparable(form)) !== JSON.stringify(comparable(baseline));
}

/** True when only the Payroll Tax Registration tab differs from `baseline`. */
export function isRegistrationDirty(form: LocationFormState, baseline: LocationFormState): boolean {
  return JSON.stringify(comparable(form).registration) !== JSON.stringify(comparable(baseline).registration);
}

export type FormErrors = Record<string, string>;

const ZIP = /^\d{5}(-\d{4})?$/;
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9 .\-/]*$/;
const EARLIEST_DATE = '1990-01-01';

function latestDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/** What an existing record already holds, so "Registered needs an account on file" counts a masked number the admin has not retyped. */
export interface OnFile {
  stateAccount: boolean;
  suiAccount: boolean;
}

export function onFileFrom(location: WorkLocationResponse | null): OnFile {
  const reg = location?.taxRegistration;
  return { stateAccount: !!reg?.stateAccountNumberMasked, suiAccount: !!reg?.suiAccountNumberMasked };
}

export function validateBranchDetails(form: LocationFormState, isMain: boolean): FormErrors {
  const errors: FormErrors = {};
  if (form.name.trim().length < 2 || form.name.trim().length > 80) errors.name = 'Enter a branch name (2–80 characters).';
  if (!isMain && form.locationType === '') errors.locationType = 'Select a branch type.';
  if (form.addressLine1.trim().length < 3) errors.addressLine1 = 'Enter a physical street address (3–100 characters).';
  if (!form.country) errors.country = 'Select a country.';
  if (!form.state) errors.state = 'Select a state.';
  if (form.city.trim().length < 2) errors.city = 'Enter a city name (2–60 characters).';
  if (!ZIP.test(form.zip)) errors.zip = 'Enter a 5-digit ZIP or ZIP+4.';
  if (!form.mailingSame) {
    if (form.mailingLine1.trim().length < 3) errors.mailingLine1 = 'Enter the mailing street address.';
    if (!form.mailingCountry) errors.mailingCountry = 'Select a country.';
    if (!form.mailingState) errors.mailingState = 'Select a state.';
    if (form.mailingCity.trim().length < 2) errors.mailingCity = 'Enter a city name.';
    if (!ZIP.test(form.mailingZip)) errors.mailingZip = 'Enter a 5-digit ZIP or ZIP+4.';
  }
  return errors;
}

export function validateRegistration(form: LocationFormState, onFile: OnFile): FormErrors {
  const errors: FormErrors = {};
  const r = form.registration;
  if (r.status === '') errors.status = 'Select a registration status.';

  const checkDate = (key: 'effectiveDate' | 'nexusEstablishedDate', label: string) => {
    const value = r[key];
    if (value && (value < EARLIEST_DATE || value > latestDate())) errors[key] = `${label} must be between 01/01/1990 and one year from today.`;
  };
  checkDate('effectiveDate', 'The effective date');
  checkDate('nexusEstablishedDate', 'The nexus date');

  const accounts: Array<[keyof RegistrationFormState, string]> = [
    ['stateAccount', 'State account number'], ['suiAccount', 'SUI account number'], ['sdiPflAccount', 'SDI / PFL account number'],
    ['localRegistration', 'Local tax registration'], ['localAccount', 'Local tax account number'],
  ];
  for (const [key, label] of accounts) {
    const value = r[key].trim();
    if (value && (value.length > 50 || !IDENTIFIER.test(value))) {
      errors[key] = `${label} may contain only letters, numbers, spaces, dots, dashes and slashes, up to 50 characters.`;
    }
  }

  if (r.status === 'Registered') {
    if (!r.effectiveDate) errors.effectiveDate = 'Enter the date this registration takes effect.';
    const hasState = r.stateAccount.trim() !== '' || onFile.stateAccount;
    const hasSui = r.suiAccount.trim() !== '' || onFile.suiAccount;
    if (!hasState && !hasSui && !errors.stateAccount) errors.stateAccount = 'Enter the state account number (SEIN) or the SUI account number.';
  }
  if (r.status === 'NotRequired' && r.notes.trim() === '') errors.notes = 'Enter the compliance reason this location does not need a registration.';
  if (r.notes.trim().length > MAX_NOTES_LENGTH) errors.notes = `Notes must be ${MAX_NOTES_LENGTH} characters or fewer.`;
  return errors;
}

export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Labels of the required Branch Details fields still missing, for the Add Branch button's accessible explanation. */
export function missingRequiredLabels(form: LocationFormState, isMain: boolean): string[] {
  const labels: Record<string, string> = {
    name: 'Branch Name', locationType: 'Branch Type', addressLine1: 'Address Line 1', country: 'Country', state: 'State', city: 'City', zip: 'ZIP Code',
    mailingLine1: 'Mailing Address Line 1', mailingState: 'Mailing State', mailingCity: 'Mailing City', mailingZip: 'Mailing ZIP Code',
  };
  return Object.keys(validateBranchDetails(form, isMain)).map((k) => labels[k] ?? k);
}

function registrationRequest(form: LocationFormState): SaveTaxRegistrationRequest {
  const r = form.registration;
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    status: TAX_REGISTRATION_STATUS[(r.status || 'NotRegistered') as TaxRegistrationStatusName],
    effectiveDate: orNull(r.effectiveDate),
    nexusEstablishedDate: orNull(r.nexusEstablishedDate),
    // null = keep the number already on file; typing replaces it.
    stateAccountNumber: orNull(r.stateAccount),
    suiAccountNumber: orNull(r.suiAccount),
    sdiPflAccountNumber: orNull(r.sdiPflAccount),
    localTaxRegistration: orNull(r.localRegistration),
    localTaxAccountNumber: orNull(r.localAccount),
    notes: orNull(r.notes),
  };
}

/**
 * The request for a save. `existing` null = adding. When editing, the registration is sent only if that tab changed (so an edit of the
 * address never creates a registration record), and `expectedVersion` carries the version the form loaded.
 */
export function toRequest(
  form: LocationFormState, existing: WorkLocationResponse | null, baseline: LocationFormState,
): WorkLocationRequest {
  const request: WorkLocationRequest = {
    name: form.name.trim(),
    locationType: (form.locationType === '' ? WORK_LOCATION_TYPE.Branch : form.locationType) as WorkLocationTypeValue,
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || null,
    city: form.city.trim(),
    state: form.state,
    zip: form.zip,
    country: form.country,
    county: form.county.trim() || null,
    phone: existing?.phone ?? null,
    activeForPayroll: existing?.isDefault ? true : form.activeForPayroll,
    mailingAddress: form.mailingSame
      ? { sameAsPhysical: true, addressLine1: null, addressLine2: null, city: null, state: null, zip: null, country: null }
      : {
          sameAsPhysical: false,
          addressLine1: form.mailingLine1.trim(),
          addressLine2: form.mailingLine2.trim() || null,
          city: form.mailingCity.trim(),
          state: form.mailingState,
          zip: form.mailingZip,
          country: form.mailingCountry,
        },
  };
  if (existing === null || isRegistrationDirty(form, baseline)) request.taxRegistration = registrationRequest(form);
  if (existing !== null) request.expectedVersion = existing.version;
  return request;
}
